import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Share2, Heart, MessageCircle, Calendar, User, Edit, Trash, AlertTriangle, ZoomIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { profileThumb, profileThumbFallback } from '../utils/imageHelpers';
import ShareModal from '../components/ShareModal';
import SignupInviteModal from '../components/SignupInviteModal';
import ImageLightbox from '../components/ImageLightbox';
import RichDescription from '../components/RichDescription';
import { updatePostMeta } from '../utils/metaUtils';
import api from '../services/api';
import trackingService from '../services/tracking';

const PostViewer = () => {
    const { username, postId } = useParams();
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [shareModal, setShareModal] = useState({ isOpen: false, post: null });
    // The attachment currently open in the full-image lightbox, or null when closed —
    // lets people view an image full-size without leaving the post for its media page.
    const [lightboxAttachment, setLightboxAttachment] = useState(null);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [loadingComments, setLoadingComments] = useState(false);
    const [submittingComment, setSubmittingComment] = useState(false);
    const [signupInvite, setSignupInvite] = useState({ isOpen: false, contentType: 'post' });
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, commentId: null, commentContent: '' });
    const [isEditingPost, setIsEditingPost] = useState(false);
    const [editPostContent, setEditPostContent] = useState('');
    const [savingPostEdit, setSavingPostEdit] = useState(false);
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editCommentContent, setEditCommentContent] = useState('');
    const [savingCommentEdit, setSavingCommentEdit] = useState(false);

    useEffect(() => {
        const fetchPost = async () => {
            try {
                const response = await api.get(`/posts/${postId}`);
                
                if (response.data.success) {
                    setPost(response.data.data);
                    
                    // Update page meta tags for SEO and social sharing
                    updatePostMeta(response.data.data).catch(console.error);
                    
                    // Track page view
                    trackingService.trackPageView('other', postId);
                } else {
                    setError('Post not found');
                }
            } catch (err) {
                console.error('Error fetching post:', err);
                setError('Failed to load post');
            } finally {
                setLoading(false);
            }
        };

        const fetchComments = async () => {
            try {
                setLoadingComments(true);
                const response = await api.get(`/posts/${postId}/comments`);
                
                if (response.data.success) {
                    setComments(response.data.data);
                }
            } catch (err) {
                console.error('Error fetching comments:', err);
            } finally {
                setLoadingComments(false);
            }
        };

        if (postId) {
            fetchPost();
            fetchComments();
        }
    }, [postId]);

    const handleLike = async () => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: 'post' });
            return;
        }

        try {
            const response = await api.post(`/posts/${postId}/like`);
            
            if (response.data.success) {
                setPost(prev => ({
                    ...prev,
                    like_count: response.data.data.likeCount,
                    user_has_liked: response.data.data.liked
                }));
            }
        } catch (err) {
            console.error('Error toggling like:', err);
        }
    };

    const handleCommentLike = async (commentId) => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: 'post' });
            return;
        }

        try {
            const response = await api.post(`/posts/comments/${commentId}/like`);
            
            if (response.data.success) {
                setComments(prev => prev.map(comment => 
                    comment.id === commentId
                        ? { ...comment, like_count: response.data.data.likeCount, user_has_liked: response.data.data.liked }
                        : comment
                ));
            }
        } catch (err) {
            console.error('Error toggling comment like:', err);
        }
    };

    const handleAddComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim() || !isAuthenticated) return;

        try {
            setSubmittingComment(true);
            const response = await api.post(`/posts/${postId}/comments`, {
                content: newComment.trim()
            });
            
            if (response.data.success) {
                setComments(prev => [response.data.data, ...prev]);
                setNewComment('');
                setPost(prev => ({
                    ...prev,
                    comment_count: prev.comment_count + 1
                }));
            }
        } catch (err) {
            console.error('Error adding comment:', err);
        } finally {
            setSubmittingComment(false);
        }
    };

    const handleDeleteComment = async (commentId) => {
        try {
            const response = await api.delete(`/posts/comments/${commentId}`);
            
            if (response.data.success) {
                setComments(prev => prev.filter(comment => comment.id !== commentId));
                setPost(prev => ({
                    ...prev,
                    comment_count: Math.max(0, prev.comment_count - 1)
                }));
                setDeleteConfirm({ isOpen: false, commentId: null, commentContent: '' });
            }
        } catch (err) {
            console.error('Error deleting comment:', err);
        }
    };

    const canDeleteComment = (comment) => {
        if (!isAuthenticated || !user) return false;
        // User can delete if they are the comment author OR the post author
        return comment.user_id === user.id || post?.user_id === user.id;
    };

    const canEditComment = (comment) => {
        if (!isAuthenticated || !user) return false;
        // Only the comment author can edit their own comment
        return comment.user_id === user.id;
    };

    const handleEditPostStart = () => {
        setEditPostContent(post.content);
        setIsEditingPost(true);
    };

    const handleEditPostCancel = () => {
        setIsEditingPost(false);
        setEditPostContent('');
    };

    const handleSavePostEdit = async () => {
        if (!editPostContent.trim()) return;

        try {
            setSavingPostEdit(true);
            const response = await api.put(`/posts/${postId}`, { content: editPostContent.trim() });

            if (response.data.success) {
                setPost(prev => ({ ...prev, content: response.data.data.content }));
                setIsEditingPost(false);
                setEditPostContent('');
            }
        } catch (err) {
            console.error('Error updating post:', err);
        } finally {
            setSavingPostEdit(false);
        }
    };

    const handleEditCommentStart = (comment) => {
        setEditingCommentId(comment.id);
        setEditCommentContent(comment.content);
    };

    const handleEditCommentCancel = () => {
        setEditingCommentId(null);
        setEditCommentContent('');
    };

    const handleSaveCommentEdit = async (commentId) => {
        if (!editCommentContent.trim()) return;

        try {
            setSavingCommentEdit(true);
            const response = await api.put(`/posts/comments/${commentId}`, { content: editCommentContent.trim() });

            if (response.data.success) {
                setComments(prev => prev.map(comment =>
                    comment.id === commentId ? { ...comment, content: response.data.data.content } : comment
                ));
                setEditingCommentId(null);
                setEditCommentContent('');
            }
        } catch (err) {
            console.error('Error updating comment:', err);
        } finally {
            setSavingCommentEdit(false);
        }
    };

    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);

        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
        if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)}w ago`;
        return date.toLocaleDateString();
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="loading-spinner w-12 h-12 mx-auto mb-4"></div>
                    <p className="text-gray-400">Loading post...</p>
                </div>
            </div>
        );
    }

    if (error || !post) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-white mb-4">Post Not Found</h2>
                    <p className="text-gray-400 mb-8">{error || 'The post you\'re looking for doesn\'t exist.'}</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900">
            {/* Header */}
            <div className="bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Back
                    </button>
                    
                    <div className="flex items-center gap-4">
                        {isAuthenticated && user?.id === post.user_id && !isEditingPost && (
                            <button
                                onClick={handleEditPostStart}
                                className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700 transition-colors"
                                title="Edit Post"
                            >
                                <Edit className="w-5 h-5" />
                            </button>
                        )}
                        <button
                            onClick={() => setShareModal({ isOpen: true, post })}
                            className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700 transition-colors"
                            title="Share Post"
                        >
                            <Share2 className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Post Content */}
            <div className="max-w-4xl mx-auto px-6 py-8">
                <div className="glass-card p-8 border border-purple-500/30">
                    {/* Post Header */}
                    <div className="flex items-start justify-between mb-6">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center">
                                {post.author_avatar ? (
                                    <img
                                        src={post.author_avatar}
                                        alt={post.author_display_name}
                                        width="48"
                                        height="48"
                                        className="w-12 h-12 rounded-full object-cover"
                                    />
                                ) : (
                                    <span className="text-white font-semibold text-lg">
                                        {(post.author_display_name || post.author_username)?.charAt(0) || 'U'}
                                    </span>
                                )}
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-white">
                                    {post.author_display_name || post.author_username}
                                </h1>
                                <p className="text-gray-400">@{post.author_username}</p>
                                <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                                    <Calendar className="w-4 h-4" />
                                    <span>{formatTimeAgo(post.created_at)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Post Content */}
                    <div className="mb-6">
                        {isEditingPost ? (
                            <div>
                                <textarea
                                    value={editPostContent}
                                    onChange={(e) => setEditPostContent(e.target.value)}
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg p-4 text-lg text-white placeholder-gray-400 resize-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                                    rows="5"
                                    autoFocus
                                />
                                <div className="flex justify-end gap-2 mt-3">
                                    <button
                                        onClick={handleEditPostCancel}
                                        disabled={savingPostEdit}
                                        className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white rounded-lg transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSavePostEdit}
                                        disabled={savingPostEdit || !editPostContent.trim()}
                                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:opacity-50 text-white rounded-lg transition-colors"
                                    >
                                        {savingPostEdit ? 'Saving...' : 'Save'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <RichDescription
                                description={post.content}
                                maxLength={600}
                                showLabel={false}
                                textClassName="text-lg text-gray-200 leading-relaxed"
                            />
                        )}
                    </div>

                    {/* Post Images — stacked full-width on every screen size, rather
                        than a multi-column grid, so each image reads at full size
                        instead of being squeezed into a shared row. */}
                    {post.attachments && post.attachments.length > 0 && (
                        <div className="mb-6 space-y-4">
                            {post.attachments.map((attachment) => (
                                <button
                                    key={attachment.id}
                                    type="button"
                                    onClick={() => setLightboxAttachment(attachment)}
                                    className="group relative block w-full"
                                >
                                    <img
                                        src={attachment.optimized_url || attachment.file_url}
                                        alt={attachment.alt_text || attachment.title || 'Image'}
                                        className="w-full rounded-lg object-contain group-hover:opacity-90 transition-opacity"
                                        loading="lazy"
                                    />
                                    <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm rounded-full p-2.5 opacity-70 group-hover:opacity-100 transition-opacity">
                                        <ZoomIn className="w-5 h-5 text-white" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}

                    {lightboxAttachment && (
                        <ImageLightbox
                            src={lightboxAttachment.optimized_url || lightboxAttachment.file_url}
                            alt={lightboxAttachment.alt_text || lightboxAttachment.title || 'Image'}
                            fullPageUrl={`/view/image/${post.author_username}/${lightboxAttachment.stored_filename || lightboxAttachment.original_filename || lightboxAttachment.id}`}
                            onClose={() => setLightboxAttachment(null)}
                        />
                    )}

                    {/* Post Actions */}
                    <div className="flex items-center justify-between pt-6 border-t border-gray-700">
                        <div className="flex items-center gap-6">
                            <button
                                onClick={handleLike}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                                    post?.user_has_liked
                                        ? 'text-red-400 bg-red-400/10 hover:bg-red-400/20' 
                                        : 'text-gray-400 hover:text-red-400 hover:bg-red-400/10'
                                }`}
                            >
                                <Heart className={`w-5 h-5 ${post?.user_has_liked ? 'fill-current' : ''}`} />
                                <span>{post.like_count || 0}</span>
                            </button>

                            <div className="flex items-center gap-2 text-gray-400">
                                <MessageCircle className="w-5 h-5" />
                                <span>{post.comment_count || 0}</span>
                            </div>
                        </div>

                        <div className="text-sm text-gray-500">
                            <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full">
                                {post.post_type}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Comments Section */}
                <div className="mt-8">
                    <h3 className="text-xl font-bold text-white mb-6">
                        Comments ({post.comment_count || 0})
                    </h3>

                    {/* Add Comment Form */}
                    {isAuthenticated ? (
                        <div className="glass-card p-6 border border-gray-700 mb-6">
                            <form onSubmit={handleAddComment}>
                                <textarea
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    placeholder="Add a comment..."
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg p-4 text-white placeholder-gray-400 resize-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                                    rows="3"
                                />
                                <div className="flex justify-end mt-4">
                                    <button
                                        type="submit"
                                        disabled={!newComment.trim() || submittingComment}
                                        className="px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:opacity-50 text-white rounded-lg transition-colors"
                                    >
                                        {submittingComment ? 'Posting...' : 'Post Comment'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    ) : (
                        <div className="glass-card p-6 border border-gray-700 mb-6 text-center">
                            <div className="text-4xl mb-4">💬</div>
                            <h3 className="text-xl font-bold text-white mb-2">Join the conversation</h3>
                            <p className="text-gray-400 mb-6">
                                Login to share your thoughts and connect with other members of The Deep Wave community.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <button
                                    onClick={() => navigate('/login')}
                                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
                                >
                                    Login to Comment
                                </button>
                                <button
                                    onClick={() => navigate('/register')}
                                    className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
                                >
                                    Create Account
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Comments List */}
                    <div className="space-y-4">
                        {loadingComments ? (
                            <div className="text-center py-8">
                                <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
                                <p className="text-gray-400">Loading comments...</p>
                            </div>
                        ) : comments.length > 0 ? (
                            comments.map((comment) => (
                                <div key={comment.id} className="glass-card p-6 border border-gray-700">
                                    <div className="flex items-start gap-4">
                                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center flex-shrink-0">
                                            {comment.avatar ? (
                                                <img
                                                    src={profileThumb(comment.avatar)}
                                                    alt={comment.display_name}
                                                    width="40"
                                                    height="40"
                                                    className="w-10 h-10 rounded-full object-cover"
                                                    onError={profileThumbFallback(comment.avatar)}
                                                />
                                            ) : (
                                                <span className="text-white font-semibold text-sm">
                                                    {(comment.display_name || comment.username)?.charAt(0) || 'U'}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <h4 className="font-semibold text-white">
                                                        {comment.display_name || comment.username}
                                                    </h4>
                                                    <span className="text-sm text-gray-500">
                                                        {formatTimeAgo(comment.created_at)}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    {canEditComment(comment) && editingCommentId !== comment.id && (
                                                        <button
                                                            onClick={() => handleEditCommentStart(comment)}
                                                            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
                                                            title="Edit comment"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    {canDeleteComment(comment) && (
                                                        <button
                                                            onClick={() => setDeleteConfirm({
                                                                isOpen: true,
                                                                commentId: comment.id,
                                                                commentContent: comment.content
                                                            })}
                                                            className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                                                            title="Delete comment"
                                                        >
                                                            <Trash className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            {editingCommentId === comment.id ? (
                                                <div className="mb-3">
                                                    <textarea
                                                        value={editCommentContent}
                                                        onChange={(e) => setEditCommentContent(e.target.value)}
                                                        className="w-full bg-gray-800 border border-gray-600 rounded-lg p-3 text-white placeholder-gray-400 resize-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                                                        rows="3"
                                                        autoFocus
                                                    />
                                                    <div className="flex justify-end gap-2 mt-2">
                                                        <button
                                                            onClick={handleEditCommentCancel}
                                                            disabled={savingCommentEdit}
                                                            className="px-3 py-1 text-sm bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white rounded-lg transition-colors"
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            onClick={() => handleSaveCommentEdit(comment.id)}
                                                            disabled={savingCommentEdit || !editCommentContent.trim()}
                                                            className="px-3 py-1 text-sm bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:opacity-50 text-white rounded-lg transition-colors"
                                                        >
                                                            {savingCommentEdit ? 'Saving...' : 'Save'}
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <p className="text-gray-300 leading-relaxed mb-3">
                                                    {comment.content}
                                                </p>
                                            )}
                                            <button
                                                onClick={() => handleCommentLike(comment.id)}
                                                className={`flex items-center gap-2 px-3 py-1 rounded-lg transition-colors text-sm ${
                                                    comment.user_has_liked
                                                        ? 'text-red-400 bg-red-400/10 hover:bg-red-400/20'
                                                        : 'text-gray-400 hover:text-red-400 hover:bg-red-400/10'
                                                }`}
                                            >
                                                <Heart className={`w-4 h-4 ${comment.user_has_liked ? 'fill-current' : ''}`} />
                                                <span>{comment.like_count || 0}</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8">
                                <MessageCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                                <p className="text-gray-400">No comments yet. Be the first to comment!</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Delete Comment Confirmation Modal */}
            {deleteConfirm.isOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
                    <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full border border-red-500/30">
                        <div className="flex items-center gap-3 mb-4">
                            <AlertTriangle className="w-6 h-6 text-red-400" />
                            <h3 className="text-xl font-bold text-white">Delete Comment</h3>
                        </div>
                        <p className="text-gray-300 mb-4">
                            Are you sure you want to delete this comment? This action cannot be undone.
                        </p>
                        <div className="bg-gray-700/30 rounded-lg p-3 mb-6">
                            <p className="text-gray-400 text-sm italic">
                                "{deleteConfirm.commentContent.length > 100 
                                    ? deleteConfirm.commentContent.substring(0, 100) + '...' 
                                    : deleteConfirm.commentContent}"
                            </p>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => handleDeleteComment(deleteConfirm.commentId)}
                                className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                            >
                                Delete Comment
                            </button>
                            <button
                                onClick={() => setDeleteConfirm({ isOpen: false, commentId: null, commentContent: '' })}
                                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Share Modal */}
            {shareModal.isOpen && (
                <ShareModal
                    isOpen={shareModal.isOpen}
                    onClose={() => setShareModal({ isOpen: false, post: null })}
                    mediaType="post"
                    mediaId={post.id}
                    username={post.author_username}
                    mediaTitle={post.content.substring(0, 50) + '...'}
                    description={post.content}
                    artistName={post.author_display_name || post.author_username}
                    imageUrl={post.attachments?.[0]?.optimized_url || post.attachments?.[0]?.file_url}
                />
            )}

            {/* Signup Invite Modal */}
            <SignupInviteModal
                isOpen={signupInvite.isOpen}
                onClose={() => setSignupInvite({ isOpen: false, contentType: 'post' })}
                contentType={signupInvite.contentType}
            />
        </div>
    );
};

export default PostViewer;